import { fetchFirebaseFunction } from '@/lib/firebaseFunctions';
import {
  Message,
  Model,
} from '@shared/types';
import { QueryClient } from '@tanstack/react-query';
import i18n from '@/i18n/i18n';

// ---------- Main client-side chat function ----------

export interface ChatOptions {
  conversationId: string;
  messageId: string;
  newMessageId: string;
  model: Model;
  thinking?: boolean;
  /** When true, the agent runs to completion without stopping for per-render
   *  approval. Default false → user gets continue/modify/done buttons after
   *  each successful render. */
  turboMode?: boolean;
  queryClient: QueryClient;
  onMessage: (message: Message) => void;
}

export async function chat(opts: ChatOptions): Promise<Message> {
  const {
    conversationId,
    messageId,
    newMessageId,
    model,
    thinking,
    turboMode,
    onMessage,
  } = opts;

  console.log(`[chat] Starting. conversationId=${conversationId}, thinking=${thinking}, model=${model}, turbo=${!!turboMode}`);

  // Call the Firebase Cloud Function
  const response = await fetchFirebaseFunction('chat', {
    method: 'POST',
    body: JSON.stringify({
      messageId,
      newMessageId,
      conversationId,
      model,
      thinking,
      turboMode,
    }),
  });

  console.log(`[chat] Response status: ${response.status}`);

  if (!response.ok) {
    const errText = await response.text();
    console.error('[chat] API error:', response.status, errText);
    throw new Error(`${i18n.t('services.chatApiError', { ns: 'common' })} (${response.status}): ${errText}`);
  }

  // Process streaming newline-delimited JSON messages
  const reader = response.body?.getReader();
  if (!reader) throw new Error(i18n.t('services.chatNoBody', { ns: 'common' }));
  const decoder = new TextDecoder();
  let buffer = '';
  let lastMessage: Message | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const message = JSON.parse(line) as Message;
          // Log only when steps grow (one log per new step) so the console
          // shows what the agent JUST did, not a flood of identical
          // step-count summaries on every stream chunk.
          const prevSteps = lastMessage?.content?.steps?.length ?? 0;
          const nextSteps = message.content?.steps?.length ?? 0;
          if (nextSteps > prevSteps) {
            const newSteps = (message.content?.steps ?? []).slice(prevSteps);
            for (const step of newSteps) {
              if (step.type === 'tool_use') {
                console.log(`[chat] tool_use ${step.name}`, step.input);
              } else if (step.type === 'tool_result') {
                const obs = step.observation ?? '';
                const summary = obs.length > 240 ? obs.slice(0, 240) + '…' : obs;
                console.log(
                  `[chat] tool_result ${step.toolName} ok=${step.ok}`,
                  summary,
                );
              } else if (step.type === 'error') {
                console.error(`[chat] step error:`, step.message);
              } else if (step.type === 'checkpoint') {
                console.log(
                  `[chat] checkpoint parts=${step.partCount} joints=${step.jointCount} features=${step.featureCount ?? 0}`,
                );
              } else if (step.type === 'text' && step.text) {
                const t = step.text;
                console.log(
                  `[chat] text:`,
                  t.length > 120 ? t.slice(0, 120) + '…' : t,
                );
              }
            }
          }
          lastMessage = message;
          onMessage(message);
        } catch (e) {
          console.error('[chat] Parse error:', e, 'line:', line.substring(0, 200));
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      try {
        const message = JSON.parse(buffer) as Message;
        lastMessage = message;
        onMessage(message);
      } catch (e) {
        console.error('[chat] Final parse error:', e);
      }
    }
  } catch (err) {
    console.error('[chat] Stream error:', err);
    if (!lastMessage) {
      throw err;
    }
  }

  if (!lastMessage) {
    console.error('[chat] No messages received from stream');
    throw new Error(i18n.t('services.chatNoResponse', { ns: 'common' }));
  }

  console.log(`[chat] Stream complete. Final message has: text=${!!lastMessage.content?.text}, steps=${lastMessage.content?.steps?.length || 0}, artifact=${!!lastMessage.content?.artifact}, pendingQ=${!!lastMessage.content?.pendingQuestion}, pendingContinue=${!!lastMessage.content?.pendingContinue}`);
  return lastMessage;
}

// ─── Mid-turn interrupt ─────────────────────────────────────────────────

/** Push a steering message to the server while a turn is in flight. The
 *  server reads the interrupts subcollection between iterations and injects
 *  unconsumed text as a user message before the next model call. */
export async function sendInterrupt(params: {
  conversationId: string;
  messageId: string;
  text: string;
}): Promise<{ id: string }> {
  const response = await fetchFirebaseFunction('interrupt', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`interrupt failed (${response.status}): ${errText}`);
  }
  return (await response.json()) as { id: string };
}
